import type { DirectWriteFileMeta } from './directWriteMetadata';

const DB_NAME = 'direct-write-metadata';
const STORE_NAME = 'file-metadata';
const DB_VERSION = 1;

export interface CachedMetadata {
    id: string; // usually relativePath or fullPath
    lastModified: number;
    size: number;
    meta: DirectWriteFileMeta;
    timestamp: number; // when this record was cached
}

let dbPromise: Promise<IDBDatabase> | null = null;

function _openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id' });
            }
        };
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result);
    });
}

function openDb(): Promise<IDBDatabase> {
    if (!dbPromise) {
        dbPromise = Promise.race([
            _openDb(),
            new Promise<IDBDatabase>((_, reject) =>
                setTimeout(() => reject(new Error('IndexedDB open timeout')), 2000)
            ),
        ]).catch((err) => {
            dbPromise = null;
            throw err;
        });
    }
    return dbPromise;
}

export async function getCachedMetadata(id: string): Promise<CachedMetadata | undefined> {
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const req = store.get(id);
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    } catch (err) {
        console.warn('[MetadataDB] Failed to get metadata', err);
        return undefined;
    }
}

export async function saveCachedMetadata(id: string, size: number, lastModified: number, meta: DirectWriteFileMeta): Promise<void> {
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const record: CachedMetadata = {
                id,
                size,
                lastModified,
                meta,
                timestamp: Date.now(),
            };
            const req = store.put(record);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch (err) {
        console.warn('[MetadataDB] Failed to save metadata', err);
    }
}

export async function getBatchCachedMetadata(ids: string[]): Promise<Map<string, CachedMetadata>> {
    const result = new Map<string, CachedMetadata>();
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);

            let completed = 0;
            ids.forEach(id => {
                const req = store.get(id);
                req.onsuccess = () => {
                    if (req.result) {
                        result.set(id, req.result);
                    }
                    completed++;
                    if (completed === ids.length) resolve(result);
                };
                req.onerror = () => {
                    console.warn(`[MetadataDB] Error fetching ${id}`);
                    completed++;
                    if (completed === ids.length) resolve(result);
                };
            });

            if (ids.length === 0) resolve(result);
        });
    } catch (err) {
        console.warn('[MetadataDB] Failed batch get', err);
        return result;
    }
}

export async function clearMetadataCache(): Promise<void> {
    try {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.clear();
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch (err) {
        console.warn('[MetadataDB] Failed to clear cache', err);
    }
}
